import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { useEnregistrerPaiement } from '@/hooks/queries/useEcheances';
import { formatDA } from '@/lib/format';
import type { EcheanceRow } from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  echeance: EcheanceRow | null;
  onSaved: () => void;
}

export function EncaisserDialog({ open, onOpenChange, echeance, onSaved }: Props): JSX.Element {
  const [montant, setMontant] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const enregistrer = useEnregistrerPaiement();

  useEffect(() => {
    if (open && echeance) {
      setMontant(String(Number(echeance.montant_du)));
      setError(null);
    }
  }, [open, echeance]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!echeance) return;
    const m = Number(montant);
    if (!(m > 0)) return setError('Montant invalide');
    if (m > Number(echeance.montant_du))
      return setError(`Maximum encaissable : ${formatDA(Number(echeance.montant_du))}`);
    try {
      await enregistrer.mutateAsync({
        id: echeance.id,
        montant_paye: Number(echeance.montant_paye) + m,
      });
      toast.success(
        m === Number(echeance.montant_du)
          ? 'Échéance soldée — passée en payée'
          : `Encaissé partiellement (${formatDA(m)})`,
      );
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Encaisser sur ${echeance?.reference ?? ''}`}
      description={
        echeance
          ? `Montant restant dû : ${formatDA(Number(echeance.montant_du))} ${echeance.devise_code}`
          : ''
      }
      onSubmit={handleSubmit}
      isSubmitting={enregistrer.isPending}
      submitLabel="Enregistrer le paiement"
      size="md"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      <FormField
        label="Montant à encaisser"
        type="number"
        step="0.01"
        min="0.01"
        value={montant}
        onChange={(e) => setMontant(e.target.value)}
        required
        autoFocus
      />
      <p className="text-xs text-muted-foreground">
        Si le montant correspond exactement au restant dû, l&apos;échéance sera marquée
        «&nbsp;payée&nbsp;» automatiquement. Sinon elle passera en «&nbsp;partiellement
        payée&nbsp;».
      </p>
    </FormDialog>
  );
}
